import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  makeStyles,
  tokens,
  Button,
  MessageBar,
  shorthands,
  Spinner
} from '@fluentui/react-components';
import { Save24Regular } from '@fluentui/react-icons';
import { OfficeEmailExtractor } from '../../services/officeEmailExtractor';
import { EmailSaveAPI } from '../../services/emailSaveApi';
import { SaveOptions, SaveOptionsResponse } from '../../types/email.types';
import SaveOptionsPanel from './SaveOptionsPanel';
import SaveStatus from './SaveStatus';
import { useIdentity } from '@workspace/identity-react';

const useStyles = makeStyles({
  card: {
    ...shorthands.margin(tokens.spacingVerticalM),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
  },
  content: {
    ...shorthands.padding(tokens.spacingVerticalM),
  },
  saveButton: {
    marginTop: tokens.spacingVerticalL,
  }
});

export const EmailSaver: React.FC = () => {
  const styles = useStyles();
  const { getAccessToken } = useIdentity();
  const [api] = useState(() => new EmailSaveAPI(getAccessToken));
  
  const [saveOptions, setSaveOptions] = useState<SaveOptionsResponse | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<SaveOptions>({
    format: 'Eml',
    storageLocation: 'FileStorage'
  });
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSaveOptions();
  }, []);

  const loadSaveOptions = async () => {
    try {
      const options = await api.getSaveOptions();
      setSaveOptions(options);
      
      // Set default selections
      if (options.formats.length > 0) {
        setSelectedOptions(prev => ({ ...prev, format: options.formats[0].value }));
      }
      if (options.storageLocations.length > 0) {
        setSelectedOptions(prev => ({ ...prev, storageLocation: options.storageLocations[0].value }));
      }
    } catch (err) {
      console.error('Failed to load save options:', err);
      setError('Failed to load save options');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaveResult(null);

    try {
      // Extract email data from Outlook
      const extractor = new OfficeEmailExtractor();
      const emailData = await extractor.extractEmailData();

      // Send to backend for processing
      const result = await api.saveEmail(emailData, selectedOptions);
      
      if (result.success && result.result) {
        setSaveResult(result.result);
      } else {
        throw new Error(result.error || 'Failed to save email');
      }

    } catch (err) {
      console.error('Save failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to save email');
    } finally {
      setSaving(false);
    }
  };

  if (!saveOptions) {
    return (
      <Card className={styles.card}>
        <div className={styles.content}>
          <Spinner label="Loading save options..." />
        </div>
      </Card>
    );
  }

  return (
    <Card className={styles.card}>
      <CardHeader
        header={<h3>Save Email</h3>}
        description="Save this email to your device"
      />
      <div className={styles.content}>
        <SaveOptionsPanel
          options={saveOptions}
          selectedOptions={selectedOptions}
          onOptionsChange={setSelectedOptions}
          disabled={saving}
        />

        <Button
          className={styles.saveButton}
          appearance="primary"
          icon={<Save24Regular />}
          onClick={handleSave}
          disabled={saving}
          size="large"
        >
          {saving ? 'Saving...' : 'Save Email'}
        </Button>

        {error && (
          <MessageBar
            intent="error"
            style={{ marginTop: tokens.spacingVerticalM }}
          >
            {error}
          </MessageBar>
        )}

        {saveResult && (
          <SaveStatus result={saveResult} />
        )}
      </div>
    </Card>
  );
};

export default EmailSaver;
